import {Fragment, useEffect, useMemo, useState} from 'react';
import {ChevronRight, Lock, Pencil, Plus, RotateCcw, Trash2, X} from 'lucide-react';
import {Badge, Button, Switch, Textarea} from '@kroll-resolver-crisp/ui-components-react';
import type {
  ApprovedWorkflow,
  GroundingEntryInput,
  GroundingModality,
  GroundingPolarity,
  GroundingSection
} from '../../api/workflows';
import {ConceptTree} from './ConceptTree';
import {
  MODALITY_COPY,
  MODALITY_ORDER,
  POLARITY_COPY,
  SECTION_COPY,
  SECTION_ORDER,
  modalitiesLabel,
  type SetStage,
  type Stage
} from './workflowUi';

type Row = {
  id: string;
  concept_id: string;
  section: GroundingSection;
  polarity: GroundingPolarity;
  modalities: GroundingModality[];
  content: string;
  original: GroundingEntryInput;
  state: 'new' | 'edited' | 'retired' | null;
};

const STATE_LABEL = {new: 'New', edited: 'Edited', retired: 'Retiring'} as const;

/** Form values; `ticked` lists the checked modality boxes, so every box ticked means all modalities. */
type Draft = {
  section: GroundingSection;
  polarity: GroundingPolarity;
  ticked: GroundingModality[];
  content: string;
};

const newDraft = (section: GroundingSection): Draft => ({
  section,
  polarity: 'positive',
  ticked: [...MODALITY_ORDER],
  content: ''
});

function modalitiesOf(ticked: readonly GroundingModality[]): GroundingModality[] {
  return ticked.length === MODALITY_ORDER.length
    ? []
    : MODALITY_ORDER.filter((m) => ticked.includes(m));
}

function EntryForm({
  draft,
  onChange,
  onSave,
  onCancel,
  saveLabel,
  placeholder
}: {
  readonly draft: Draft;
  readonly onChange: (draft: Draft) => void;
  readonly onSave: () => void;
  readonly onCancel: () => void;
  readonly saveLabel: string;
  readonly placeholder?: string;
}) {
  const invalid = !draft.content.trim() || draft.ticked.length === 0;
  return (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Section"
          value={draft.section}
          className="border-input bg-background h-8 rounded-md border px-2 text-sm"
          onChange={(event) => {
            onChange({...draft, section: event.target.value as GroundingSection});
          }}>
          {SECTION_ORDER.map((value) => (
            <option key={value} value={value}>
              {SECTION_COPY[value].label}
            </option>
          ))}
        </select>
        <select
          aria-label="Polarity"
          value={draft.polarity}
          className="border-input bg-background h-8 rounded-md border px-2 text-sm"
          onChange={(event) => {
            onChange({...draft, polarity: event.target.value as GroundingPolarity});
          }}>
          {(['positive', 'negative'] as const).map((value) => (
            <option key={value} value={value}>
              {POLARITY_COPY[value]}
            </option>
          ))}
        </select>
        <fieldset className="flex items-center gap-3">
          <legend className="sr-only">Modalities</legend>
          {MODALITY_ORDER.map((modality) => (
            <label key={modality} className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={draft.ticked.includes(modality)}
                onChange={(event) => {
                  onChange({
                    ...draft,
                    ticked: event.target.checked
                      ? [...draft.ticked, modality]
                      : draft.ticked.filter((m) => m !== modality)
                  });
                }}
              />
              {MODALITY_COPY[modality]}
            </label>
          ))}
        </fieldset>
      </div>
      <Textarea
        autoFocus
        rows={3}
        value={draft.content}
        aria-label="Entry content"
        placeholder={placeholder}
        onChange={(event) => {
          onChange({...draft, content: event.target.value});
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !invalid) {
            onSave();
          }

          if (event.key === 'Escape') {
            onCancel();
          }
        }}
      />
      {draft.ticked.length === 0 ? (
        <span className="text-destructive text-xs">Tick at least one modality.</span>
      ) : null}
      <div className="flex items-center gap-1.5">
        <Button size="sm" disabled={invalid} onClick={onSave}>
          {saveLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function GroundingEditor({
  workflow,
  editable,
  locked,
  stage,
  setStage
}: {
  readonly workflow: ApprovedWorkflow;
  readonly editable: boolean;
  readonly locked: string | null;
  readonly stage: Stage;
  readonly setStage: SetStage;
}) {
  const {nodes} = workflow;
  const rootId = workflow.workflow.approved_root_concept_ids[0] ?? nodes[0]?.concept_id ?? '';
  const [selected, setSelected] = useState(rootId);
  const [withChildren, setWithChildren] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [adding, setAdding] = useState<GroundingSection | null>(null);
  const [addDraft, setAddDraft] = useState<Draft>(newDraft('definition'));

  useEffect(() => {
    setSelected(rootId);
  }, [rootId, workflow.workflow.id]);

  useEffect(() => {
    setEditing(null);
    setAdding(null);
  }, [selected, workflow.workflow.id]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.concept_id, n])), [nodes]);
  const descendants = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const node of nodes) {
      let current: (typeof nodes)[number] | undefined = node;
      while (current) {
        const set = map.get(current.concept_id) ?? new Set<string>();
        set.add(node.concept_id);
        map.set(current.concept_id, set);
        current = current.parent_concept_id ? byId.get(current.parent_concept_id) : undefined;
      }
    }

    return map;
  }, [nodes, byId]);

  const all = useMemo<Row[]>(
    () => [
      ...workflow.grounding.map((entry): Row => {
        const edited = stage.edits[entry.id];
        const current = edited ?? {
          entry_id: entry.id,
          concept_id: entry.concept_id,
          section: entry.section,
          polarity: entry.polarity,
          modalities: entry.modalities,
          content: entry.content
        };
        return {
          id: entry.id,
          concept_id: current.concept_id,
          section: current.section,
          polarity: current.polarity,
          modalities: current.modalities,
          content: current.content,
          original: {
            entry_id: entry.id,
            concept_id: entry.concept_id,
            section: entry.section,
            polarity: entry.polarity,
            modalities: entry.modalities,
            content: entry.content
          },
          state: stage.retired[entry.id] ? 'retired' : edited === undefined ? null : 'edited'
        };
      }),
      ...stage.added.map((added): Row => ({
        id: added.tmp,
        concept_id: added.concept_id,
        section: added.section,
        polarity: added.polarity,
        modalities: added.modalities,
        content: added.content,
        original: {
          entry_id: null,
          concept_id: added.concept_id,
          section: added.section,
          polarity: added.polarity,
          modalities: added.modalities,
          content: added.content
        },
        state: 'new'
      }))
    ],
    [workflow.grounding, stage]
  );

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    for (const node of nodes) {
      const within = descendants.get(node.concept_id);
      result[node.concept_id] = all.filter(
        (row) => row.state !== 'retired' && within?.has(row.concept_id)
      ).length;
    }

    return result;
  }, [all, nodes, descendants]);

  const flags = useMemo(() => {
    const result: Record<string, {staged?: boolean; nodef?: boolean; nodata?: boolean}> = {};
    for (const node of nodes) {
      const mine = all.filter((row) => row.concept_id === node.concept_id);
      result[node.concept_id] = {
        nodef: !mine.some((row) => row.section === 'definition' && row.state !== 'retired'),
        nodata: mine.every((row) => row.state === 'retired'),
        staged: mine.some((row) => row.state !== null)
      };
    }

    return result;
  }, [all, nodes]);

  const node = byId.get(selected) ?? nodes[0];
  if (!node) {
    return <p className="text-muted-foreground text-sm">No concepts in scope.</p>;
  }

  const sub = descendants.get(node.concept_id) ?? new Set([node.concept_id]);
  const inScope = withChildren ? sub : new Set([node.concept_id]);
  const list = all.filter((row) => inScope.has(row.concept_id));
  const live = list.filter((row) => row.state !== 'retired').length;
  const conceptName = (id: string) => byId.get(id)?.name ?? '';
  const crumbs = node.path.split(' / ').slice(0, -1);

  function startEdit(row: Row) {
    setEditing(row.id);
    setDraft({
      section: row.section,
      polarity: row.polarity,
      ticked: row.modalities.length === 0 ? [...MODALITY_ORDER] : [...row.modalities],
      content: row.content
    });
    setAdding(null);
  }

  function saveEdit(row: Row) {
    if (!draft?.content.trim() || draft.ticked.length === 0) {
      return;
    }

    const next: GroundingEntryInput = {
      entry_id: row.original.entry_id,
      concept_id: row.concept_id,
      section: draft.section,
      polarity: draft.polarity,
      modalities: modalitiesOf(draft.ticked),
      content: draft.content.trim()
    };
    if (row.state === 'new') {
      setStage((s) => ({
        ...s,
        added: s.added.map((a) =>
          a.tmp === row.id
            ? {
                ...a,
                section: next.section,
                polarity: next.polarity,
                modalities: next.modalities,
                content: next.content
              }
            : a
        )
      }));
    } else {
      setStage((s) => {
        const {[row.id]: _previous, ...others} = s.edits;
        const same = JSON.stringify(row.original) === JSON.stringify(next);
        return {...s, edits: same ? others : {...others, [row.id]: next}};
      });
    }

    setEditing(null);
    setDraft(null);
  }

  function retire(row: Row) {
    if (row.state === 'new') {
      setStage((s) => ({...s, added: s.added.filter((a) => a.tmp !== row.id)}));
    } else {
      setStage((s) => ({...s, retired: {...s.retired, [row.id]: true}}));
    }
  }

  function undo(row: Row) {
    setStage((s) => {
      const {[row.id]: wasRetired, ...retired} = s.retired;
      const {[row.id]: _edit, ...edits} = s.edits;
      return wasRetired ? {...s, retired} : {...s, edits};
    });
  }

  function startAdd(section: GroundingSection) {
    setAdding(section);
    setAddDraft(newDraft(section));
    setEditing(null);
  }

  function add() {
    const text = addDraft.content.trim();
    if (!text || addDraft.ticked.length === 0) {
      return;
    }

    const parts =
      addDraft.section === 'keyword'
        ? text
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
        : [text];
    const modalities = modalitiesOf(addDraft.ticked);
    setStage((s) => ({
      ...s,
      added: [
        ...s.added,
        ...parts.map((content, index) => ({
          tmp: `new-${Date.now()}-${index}`,
          concept_id: node!.concept_id,
          section: addDraft.section,
          polarity: addDraft.polarity,
          modalities,
          content
        }))
      ]
    }));
    setAdding(null);
  }

  const origin = (row: Row) =>
    row.concept_id === node.concept_id ? null : (
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground text-xs hover:underline"
        onClick={() => {
          setSelected(row.concept_id);
        }}>
        {conceptName(row.concept_id)}
      </button>
    );

  const entryRow = (row: Row) => (
    <div
      key={row.id}
      className={`group flex items-start gap-3 py-2.5 first:pt-0.5 ${row.state === 'new' || row.state === 'edited' ? 'pl-3 shadow-[inset_2px_0_0_var(--primary)]' : ''}`}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editing === row.id && draft ? (
          <EntryForm
            draft={draft}
            saveLabel="Done"
            onChange={setDraft}
            onSave={() => {
              saveEdit(row);
            }}
            onCancel={() => {
              setEditing(null);
            }}
          />
        ) : (
          <>
            {row.state === 'edited' ? (
              <del className="text-muted-foreground text-xs">{row.original.content}</del>
            ) : null}
            <div className="flex flex-wrap items-center gap-1">
              <Badge variant={row.polarity === 'negative' ? 'secondary' : 'outline'}>
                {POLARITY_COPY[row.polarity]}
              </Badge>
              <Badge variant="outline">{modalitiesLabel(row.modalities)}</Badge>
            </div>
            <p
              className={`m-0 text-sm leading-normal wrap-anywhere whitespace-pre-wrap ${row.state === 'retired' ? 'text-muted-foreground line-through' : ''}`}>
              {row.content}
            </p>
          </>
        )}
        {origin(row) || row.state ? (
          <div className="flex flex-wrap items-center gap-2">
            {origin(row)}
            {row.state ? (
              <Badge variant={row.state === 'retired' ? 'destructive' : 'outline'}>
                {STATE_LABEL[row.state]}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>
      {editable && editing !== row.id ? (
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          {row.state === 'retired' || row.state === 'edited' ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                undo(row);
              }}>
              <RotateCcw aria-hidden className="size-3.5" />
              Undo
            </Button>
          ) : null}
          {row.state === 'retired' ? null : (
            <>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit entry"
                onClick={() => {
                  startEdit(row);
                }}>
                <Pencil aria-hidden className="size-3.5" />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={row.state === 'new' ? 'Remove new entry' : 'Retire entry'}
                onClick={() => {
                  retire(row);
                }}>
                <Trash2 aria-hidden className="size-3.5" />
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );

  const chip = (row: Row) => (
    <span
      key={row.id}
      title={row.concept_id === node.concept_id ? undefined : conceptName(row.concept_id)}
      className={`bg-muted inline-flex min-h-6 items-center gap-1 rounded-full py-0.5 pr-1 pl-2.5 text-xs ${row.state === 'retired' ? 'text-muted-foreground line-through' : ''} ${row.state === 'new' || row.state === 'edited' ? 'ring-primary ring-1' : ''}`}>
      {row.polarity === 'negative' ? (
        <span className="text-destructive font-medium">{POLARITY_COPY.negative}:</span>
      ) : null}
      {editable && row.state !== 'retired' ? (
        <button
          type="button"
          className="hover:underline"
          aria-label={`Edit ${row.content}`}
          onClick={() => {
            startEdit(row);
          }}>
          {row.content}
        </button>
      ) : (
        row.content
      )}
      {row.modalities.length > 0 ? (
        <span className="text-muted-foreground">· {modalitiesLabel(row.modalities)}</span>
      ) : null}
      {row.concept_id === node.concept_id ? null : (
        <span className="text-muted-foreground">· {conceptName(row.concept_id)}</span>
      )}
      {editable ? (
        row.state === 'retired' || row.state === 'edited' ? (
          <button
            type="button"
            className="text-muted-foreground hover:bg-accent rounded-full p-0.5"
            aria-label={
              row.state === 'retired' ? `Keep ${row.content}` : `Undo edit to ${row.content}`
            }
            onClick={() => {
              undo(row);
            }}>
            <RotateCcw aria-hidden className="size-3" />
          </button>
        ) : (
          <button
            type="button"
            className="text-muted-foreground hover:bg-accent rounded-full p-0.5"
            aria-label={`Remove ${row.content}`}
            onClick={() => {
              retire(row);
            }}>
            <X aria-hidden className="size-3" />
          </button>
        )
      ) : null}
    </span>
  );

  const placeholder = (section: GroundingSection) =>
    section === 'definition'
      ? `What counts as ${node.name}?`
      : section === 'keyword'
        ? `Terms that signal ${node.name}`
        : `Describe content that belongs or looks similar to ${node.name}`;

  return (
    <div className="@container">
      <div className="grid items-start gap-4 @2xl:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
        <ConceptTree
          nodes={nodes}
          selected={node.concept_id}
          counts={counts}
          flags={flags}
          onPick={setSelected}
        />
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-col gap-1">
            {crumbs.length > 0 ? (
              <nav
                aria-label="Concept path"
                className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
                {crumbs.map((crumb, index) => {
                  const trail = crumbs.slice(0, index + 1).join(' / ');
                  const target = nodes.find((n) => n.path === trail);
                  return (
                    <Fragment key={trail}>
                      {target ? (
                        <button
                          type="button"
                          className="hover:text-foreground hover:underline"
                          onClick={() => {
                            setSelected(target.concept_id);
                          }}>
                          {crumb}
                        </button>
                      ) : (
                        <span>{crumb}</span>
                      )}
                      <ChevronRight aria-hidden className="size-3" />
                    </Fragment>
                  );
                })}
              </nav>
            ) : null}
            <div className="flex flex-wrap items-end justify-between gap-2">
              <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold">
                {node.name}
                <code
                  title="Concept ID"
                  className="bg-muted rounded px-1 font-mono text-[11px] font-normal wrap-anywhere">
                  {node.concept_id}
                </code>
              </h3>
              {sub.size > 1 ? (
                <label className="inline-flex cursor-pointer items-center gap-2.5">
                  <Switch
                    checked={withChildren}
                    aria-label="Include child concepts"
                    onCheckedChange={setWithChildren}
                  />
                  <span className="text-muted-foreground text-xs">
                    Include {sub.size - 1} child concept{sub.size > 2 ? 's' : ''}
                  </span>
                </label>
              ) : null}
            </div>
            <span className="text-muted-foreground text-xs">
              {withChildren
                ? `${live} entries across ${node.name} and its children.`
                : `${live} entr${live === 1 ? 'y' : 'ies'} on this concept.`}{' '}
              When the router assigns {node.name}, the classifier gets this concept’s grounding plus
              everything under it ({counts[node.concept_id] ?? 0}).
            </span>
            {locked ? (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <Lock aria-hidden className="size-3" />
                {locked}
              </span>
            ) : null}
          </div>
          {SECTION_ORDER.map((section) => {
            const items = list.filter((row) => row.section === section);
            const count = items.filter((row) => row.state !== 'retired').length;
            const editingKeyword =
              section === 'keyword' ? items.find((row) => row.id === editing) : undefined;
            return (
              <section
                key={section}
                aria-label={SECTION_COPY[section].title}
                className="bg-card border-border flex flex-col gap-2.5 rounded-lg border px-3.5 py-3">
                <div className="flex min-h-7 flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold">{SECTION_COPY[section].title}</h4>
                  <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
                  <span className="text-muted-foreground min-w-30 flex-1 text-xs">
                    {SECTION_COPY[section].description}
                  </span>
                </div>
                {items.length > 0 ? (
                  section === 'keyword' ? (
                    <div className="flex flex-wrap gap-1.5">{items.map(chip)}</div>
                  ) : (
                    <div className="flex flex-col">{items.map(entryRow)}</div>
                  )
                ) : null}
                {editingKeyword && draft ? (
                  <EntryForm
                    draft={draft}
                    saveLabel="Done"
                    onChange={setDraft}
                    onSave={() => {
                      saveEdit(editingKeyword);
                    }}
                    onCancel={() => {
                      setEditing(null);
                    }}
                  />
                ) : null}
                {adding === section ? (
                  <EntryForm
                    draft={addDraft}
                    saveLabel={`Add to ${node.name}`}
                    placeholder={placeholder(addDraft.section)}
                    onChange={setAddDraft}
                    onSave={add}
                    onCancel={() => {
                      setAdding(null);
                    }}
                  />
                ) : null}
                {items.length === 0 && adding !== section ? (
                  <p className="text-muted-foreground m-0 text-xs">None yet.</p>
                ) : null}
                {editable && adding !== section ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="self-start"
                    aria-label={`Add ${SECTION_COPY[section].label.toLowerCase()}`}
                    onClick={() => {
                      startAdd(section);
                    }}>
                    <Plus aria-hidden className="size-3.5" /> Add
                  </Button>
                ) : null}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
